import { Phone, Mail, Clock, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { BRAND, IMAGES } from "@/data/site";
import { useT } from "@/i18n/I18nProvider";
import { useSite } from "@/i18n/useSite";
import { PageIntro, Reveal } from "@/components/Section";
import { EnquiryForm } from "@/components/EnquiryForm";

export default function Contact() {
  const t = useT();
  const { BRAND: brand } = useSite();
  const cards = [
    { icon: Phone, label: t("Call us"), value: BRAND.phone.number, href: `tel:${BRAND.phone.tel}`, testId: "contact-phone", tile: "tile-coral", light: true },
    { icon: Mail, label: t("Email us"), value: BRAND.email, href: `mailto:${BRAND.email}`, testId: "contact-email", tile: "tile-blue", light: true },
    { icon: Clock, label: t("Office hours"), value: brand.hours, testId: "contact-hours", tile: "tile-sun", light: false },
  ];
  return (
    <>
      <PageIntro eyebrow={t("Contact")} title={t("Let's talk")} accent={t("about your care.")} text={t("Tell us about your needs and what matters to you. Together, we can explore support that fits your life.")} image={IMAGES.highCare} />
      <section className="py-16 lg:py-24" data-testid="contact-section">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 space-y-4">
            {cards.map((c, i) => (
              <Reveal key={c.testId} delay={i * 0.07}>
                <div className={`${c.tile} p-6 flex items-center gap-5 ${i % 2 ? "corner-bl-sm" : "corner-tr-sm"}`} data-testid={c.testId}>
                  <span className={`w-12 h-12 rounded-md flex items-center justify-center shrink-0 ${c.light ? "bg-white/20 text-white" : "bg-white text-[var(--blue)]"}`}><c.icon size={20} /></span>
                  <div>
                    <div className={`text-xs font-semibold uppercase tracking-wide ${c.light ? "text-white/80" : "text-[var(--ink)]/70"}`}>{c.label}</div>
                    {c.href ? <a href={c.href} className={`font-semibold text-lg break-all ${c.light ? "text-white" : "text-[var(--ink)]"}`}>{c.value}</a> : <div className="font-semibold text-lg text-[var(--ink)]">{c.value}</div>}
                  </div>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.25}>
              <div className="bg-[var(--blue-tint)] p-6 corner-tr-sm">
                <h3 className="!text-[var(--blue)] text-lg font-semibold">{t("Ready to make a referral?")}</h3>
                <p className="text-sm text-[var(--body)] mt-1">{t("Complete our online referral form and we'll be in touch.")}</p>
                <Link to="/referral-form" className="btn btn-coral mt-5 text-sm" data-testid="contact-referral">{t("Referral form")} <ArrowRight size={15} /></Link>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.1} className="lg:col-span-7">
            <div className="bg-white border-2 border-[var(--blue-tint)] corner-tr p-7 sm:p-10" data-testid="enquiry-form-card">
              <h2 className="text-2xl font-semibold">{t("Send a quick enquiry")}</h2>
              <p className="text-sm text-[var(--muted)] mt-1 mb-7">{t("We usually respond within one business day.")}</p>
              <EnquiryForm sourcePage="contact" />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
